import React, { RefObject, useMemo } from 'react';

import { GENERATIVE_AI_FEATURE_TOKEN } from '@lumapps/ai-generative-content/constants';
import { useClassnames } from '@lumapps/classnames';
import { selectSpaceOrCommunityId } from '@lumapps/communities/ducks/selectors';
import { customerIdSelector } from '@lumapps/customer/ducks/selectors';
import { isFeatureEnabled } from '@lumapps/features';
import { Avatar, InputHelper, Kind, Theme, ThemeProvider, useTheme } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { getInstanceColorPalette } from '@lumapps/style/ducks/selectors';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { inputLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { getAvatarUrl } from '@lumapps/user/utils/getUserProfilePicture';
import { useId } from '@lumapps/utils/hooks/useId';
import { ImageEditorOptions } from '@lumapps/wrex-enhanced-image/types';
import { WrexFileContextProvider } from '@lumapps/wrex-file/context';
import { GenerativeAIToolbar } from '@lumapps/wrex-generative-content/components/GenerativeAIToolbar/GenerativeAIToolbar';
import type { Wrex } from '@lumapps/wrex/types';

import { STRUCTURED_CONTENT_WREX_EDITOR_CLASSNAME } from '../../constants';
import { createStructuredContentEditor } from './createStructuredContentEditor';
import { StructuredContentToolbar } from './StructuredContentToolbar/StructuredContentToolbar';

import './index.scss';

export interface StructuredContentEditorProps {
    wrapperRef?: RefObject<HTMLDivElement>;
    inputRef?: RefObject<HTMLDivElement>;
    className?: string;
    placeholder: string;
    isRequired?: boolean;
    value: Wrex.Nodes;
    onChange: (content: Wrex.Nodes) => void;
    uploadFunction: ImageEditorOptions['uploadFunction'];
    parentDialogRef?: RefObject<HTMLDivElement>;
    isReadOnly?: boolean;
    hasError?: boolean;
    name?: string;
    /** String key to force the slate value to update through a re-render */
    rerenderKey?: string;
    /** Border variant */
    variant?: 'borderless' | 'regular';
    /** Editable HTML id */
    editableId?: string;
}

const CLASSNAME = 'structured-content-editor';

export const StructuredContentEditor: React.FC<StructuredContentEditorProps> = React.memo(
    ({
        className,
        placeholder,
        value,
        onChange,
        uploadFunction,
        isRequired,
        wrapperRef,
        inputRef,
        parentDialogRef,
        isReadOnly,
        hasError,
        name,
        rerenderKey: propsRerenderKey,
        variant = 'regular',
        editableId,
        ...props
    }) => {
        const { block, element } = useClassnames(CLASSNAME);
        const editorClass = useClassnames(STRUCTURED_CONTENT_WREX_EDITOR_CLASSNAME);
        const theme = useTheme() || Theme.light;
        const { translateKey } = useTranslate();
        const colorPalette = useSelector(getInstanceColorPalette);
        const customerId = useSelector(customerIdSelector);
        const communityId = useSelector(selectSpaceOrCommunityId);
        const isGenerativeAiEnabled = useSelector(isFeatureEnabled(GENERATIVE_AI_FEATURE_TOKEN));
        const isContributionAlphaEnabled = useSelector(isFeatureEnabled('contribution-alpha'));
        const inputLanguage = useSelector(inputLanguageSelector);
        const [rerenderKey, setRerenderKey] = React.useState(propsRerenderKey);
        const errorId = useId();
        const localRef = React.useRef<HTMLDivElement>(null);
        const editorRef = wrapperRef || localRef;

        const { EditorContext, Editable } = useMemo(
            () =>
                createStructuredContentEditor({
                    // File upload function
                    uploadFunction,
                    placeholder,
                    inlineAutoCompleteParams: (user: { id: string; fullName: string }) => ({
                        before: <Avatar image={getAvatarUrl(user.id, customerId)} size="s" alt="" />,
                    }),
                    htmlPasteOptions: { colorPalette },
                    isContributionAlphaEnabled,
                    communityId,
                }),
            [colorPalette, communityId, customerId, isContributionAlphaEnabled, placeholder, uploadFunction],
        );

        /**
         * We set an unchanged value to avoid rerendering the WrexFileContextProvider
         */
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const initialValue = React.useMemo(() => value, [rerenderKey]);

        /**
         * We make it for refresh editor in designer simple mode on change the language
         */
        React.useEffect(() => {
            setRerenderKey(inputLanguage);
        }, [inputLanguage]);

        React.useEffect(() => {
            if (propsRerenderKey) {
                setRerenderKey(propsRerenderKey);
            }
        }, [propsRerenderKey]);

        return (
            <EditorContext initialValue={value} onChange={onChange} key={rerenderKey}>
                <div
                    {...props}
                    className={block(
                        {
                            'is-required': !!isRequired,
                            'has-error': !!hasError,
                            [`variant-${variant}`]: true,
                        },
                        [className],
                    )}
                    ref={editorRef}
                >
                    <ThemeProvider value={undefined}>
                        <StructuredContentToolbar
                            className={element('top-toolbar')}
                            parentDialogRef={parentDialogRef}
                            isReadOnly={isReadOnly}
                        />
                    </ThemeProvider>

                    <WrexFileContextProvider content={initialValue}>
                        <Editable
                            id={editableId}
                            ref={inputRef}
                            className={element('editor', [
                                editorClass.block({ [`theme-${theme}`]: !!theme }),
                                className,
                            ])}
                            readOnly={isReadOnly}
                            aria-readonly={isReadOnly}
                            aria-describedby={hasError && isRequired ? errorId : undefined}
                            name={name}
                        />
                    </WrexFileContextProvider>

                    {isGenerativeAiEnabled && !isReadOnly && (
                        <ThemeProvider value={undefined}>
                            <GenerativeAIToolbar
                                className={element('bottom-toolbar')}
                                parentDialogRef={parentDialogRef}
                                editorRef={editorRef}
                            />
                        </ThemeProvider>
                    )}
                </div>
                {hasError && isRequired && (
                    <InputHelper kind={Kind.error} theme={theme} id={errorId}>
                        {translateKey(GLOBAL.FIELD_IS_REQUIRED)}
                    </InputHelper>
                )}
            </EditorContext>
        );
    },
);
StructuredContentEditor.displayName = 'StructuredContentEditor';
